import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getReservations,
  createReservation,
  updateReservation,
  deleteReservation,
  getReservationById,
} from '../services/reservationService';
import Notification from '../components/Common/Notification';

// Create and export the context
export const ReservationContext = createContext();

// Context Provider component
export const ReservationProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  // Function to fetch authentication headers
  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Function to show notifications
  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  // Function to close notifications
  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  // Function to fetch all reservations
  const fetchAllReservations = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getReservations(authHeaders);
      setReservations(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching reservations.';
      showNotification(errorMessage, true);
    }
  };

  // Function to fetch a reservation by ID
  const fetchReservationById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getReservationById(id, authHeaders);
      setSelectedReservation(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching reservation with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // Function to add a new reservation
  const addReservation = async (reservationData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newReservation = await createReservation(reservationData, authHeaders);
      setReservations(prevReservations => [...prevReservations, newReservation]);
      showNotification('Reservation added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding reservation.';
      showNotification(errorMessage, true);
    }
  };

  // Function to edit an existing reservation
  const editReservation = async (id, reservationData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedReservation = await updateReservation(id, reservationData, authHeaders);
      setReservations(prevReservations =>
        prevReservations.map(reservation => (reservation.id === id ? updatedReservation : reservation))
      );
      showNotification('Reservation updated successfully!', false);
      return updatedReservation;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating reservation.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  // Function to remove a reservation
  const removeReservation = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteReservation(id, authHeaders);
      setReservations(prevReservations => prevReservations.filter(reservation => reservation.id !== id));
      showNotification('Reservation removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing reservation.';
      showNotification(errorMessage, true);
    }
  };


  // useEffect(() => {
  //   fetchAllReservations();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <ReservationContext.Provider
      value={{
        reservations,
        selectedReservation,
        fetchAllReservations,
        fetchReservationById,
        addReservation,
        editReservation,
        removeReservation,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </ReservationContext.Provider>
  );
};

// Custom hook to use the ReservationContext
export const useReservation = () => useContext(ReservationContext);
